(() => {
  const productCache = new Map();

  const productPath = (href) => {
    try {
      const url = new URL(href, window.location.href);
      const match = url.pathname.match(/\/products\/([^/]+)/i);
      return match ? `/products/${match[1]}` : null;
    } catch (_error) {
      return null;
    }
  };

  const loadProduct = (path) => {
    if (!productCache.has(path)) {
      const root = window.Shopify?.routes?.root || '/';
      const normalizedPath = path.replace(/^\//, '');
      productCache.set(path, fetch(`${root}${normalizedPath}.js`, { credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error(`Product request failed: ${response.status}`);
          return response.json();
        })
        .catch(() => null));
    }
    return productCache.get(path);
  };

  const imageUrl = (image) => typeof image === 'string' ? image : image?.src;

  const buildMedia = (primary, secondaryUrl, title) => {
    const media = document.createElement('span');
    media.className = 'ssa-product-media';
    primary.classList.add('ssa-product-media__image', 'ssa-product-media__image--primary');
    media.appendChild(primary);

    if (secondaryUrl) {
      const secondary = document.createElement('img');
      secondary.className = 'ssa-product-media__image ssa-product-media__image--secondary';
      secondary.src = secondaryUrl;
      secondary.alt = '';
      secondary.setAttribute('aria-hidden', 'true');
      secondary.loading = 'lazy';
      secondary.decoding = 'async';
      if (primary.width) secondary.width = primary.width;
      if (primary.height) secondary.height = primary.height;
      media.classList.add('ssa-product-media--has-secondary');
      media.appendChild(secondary);
    }

    if (!primary.alt) primary.alt = title || '';
    return media;
  };

  const enhanceImageLink = async (link, path) => {
    if (link.dataset.ssaProductEnhanced === 'true') return;
    link.dataset.ssaProductEnhanced = 'true';
    const primary = link.querySelector('img');
    if (!primary) return;
    const product = await loadProduct(path);
    if (!product) return;
    const primaryUrl = primary.currentSrc || primary.src;
    const secondary = (product.images || [])
      .map(imageUrl)
      .find((url, index) => index > 0 && url && !primaryUrl.includes(url.split('?')[0]));
    const media = buildMedia(primary, secondary, product.title);
    link.prepend(media);
    link.classList.add('ssa-product-image-link');
  };

  const productCard = (product, href) => {
    const link = document.createElement('a');
    link.className = 'ssa-mentioned-product';
    link.href = href;
    const primaryUrl = imageUrl(product.featured_image) || imageUrl(product.images?.[0]);
    if (!primaryUrl) return null;
    const primary = document.createElement('img');
    primary.src = primaryUrl;
    primary.alt = product.title || '';
    primary.loading = 'lazy';
    primary.decoding = 'async';
    primary.width = 900;
    primary.height = 900;
    const secondaryUrl = (product.images || []).map(imageUrl).find((url, index) => index > 0 && url);
    link.appendChild(buildMedia(primary, secondaryUrl, product.title));
    const title = document.createElement('span');
    title.className = 'ssa-mentioned-product__title';
    title.textContent = product.title;
    link.appendChild(title);
    return link;
  };

  const initProductMedia = async (content, root) => {
    const productLinks = [...content.querySelectorAll('a[href*="/products/"]')]
      .map((link) => ({ link, path: productPath(link.href) }))
      .filter((item) => item.path);
    if (!productLinks.length) return;

    const pathsWithImages = new Set(productLinks.filter(({ link }) => link.querySelector('img')).map(({ path }) => path));
    await Promise.all(productLinks.filter(({ link }) => link.querySelector('img')).map(({ link, path }) => enhanceImageLink(link, path)));

    if (root?.querySelector('[data-ssa-mentioned-products-ssr]')) return;

    const missing = [...new Map(productLinks
      .filter(({ path }) => !pathsWithImages.has(path))
      .map(({ link, path }) => [path, { path, href: link.href }])).values()];
    if (!missing.length) return;

    const locale = document.documentElement.lang?.slice(0, 2).toLowerCase();
    const labels = locale === 'fr'
      ? { title: 'Produits cités dans cet article', hint: 'Passez la souris sur une photo pour voir le deuxième visuel.' }
      : locale === 'es'
        ? { title: 'Productos mencionados en este artículo', hint: 'Pasa el cursor sobre una foto para ver la segunda imagen.' }
        : { title: 'Products mentioned in this article', hint: 'Hover over a product photo to see its second image.' };
    const cards = (await Promise.all(missing.map(async ({ path, href }) => {
      const product = await loadProduct(path);
      return product ? productCard(product, href) : null;
    }))).filter(Boolean);
    if (!cards.length) return;

    const section = document.createElement('section');
    section.className = 'ssa-mentioned-products';
    section.setAttribute('aria-label', labels.title);
    const heading = document.createElement('h2');
    heading.textContent = labels.title;
    const hint = document.createElement('p');
    hint.className = 'ssa-mentioned-products__hint';
    hint.textContent = labels.hint;
    const grid = document.createElement('div');
    grid.className = 'ssa-mentioned-products__grid';
    cards.forEach((card) => grid.appendChild(card));
    section.append(heading, hint, grid);
    let insertionAnchor = productLinks[productLinks.length - 1]?.link || null;
    while (insertionAnchor?.parentElement && insertionAnchor.parentElement !== content) {
      insertionAnchor = insertionAnchor.parentElement;
    }
    if (insertionAnchor?.parentElement === content) insertionAnchor.insertAdjacentElement('afterend', section);
    else content.appendChild(section);
  };

  const slugify = (value) => value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 72);

  const initArticle = (root) => {
    const content = root.querySelector('[data-ssa-content]');
    const toc = root.querySelector('[data-ssa-toc]');
    const list = root.querySelector('[data-ssa-toc-list]');
    const aside = root.querySelector('[data-ssa-aside]');
    const layout = root.querySelector('[data-ssa-layout]');

    if (!content) return;
    initProductMedia(content, root);
    if (!toc || !list) return;

    const headings = [...content.querySelectorAll('h2, h3')]
      .filter((heading) => heading.textContent.trim().length > 0);

    if (headings.length < 2) {
      toc.hidden = true;
      if (aside && !aside.querySelector('.ssa-side-cta')) {
        aside.hidden = true;
        layout?.classList.add('ssa-layout--single');
      }
      return;
    }

    const used = new Set();
    headings.forEach((heading, index) => {
      let id = heading.id || slugify(heading.textContent) || `section-${index + 1}`;
      const base = id;
      let suffix = 2;
      while (used.has(id) || (document.getElementById(id) && document.getElementById(id) !== heading)) {
        id = `${base}-${suffix++}`;
      }
      used.add(id);
      heading.id = id;

      const item = document.createElement('li');
      if (heading.tagName === 'H3') item.className = 'is-sub';
      const link = document.createElement('a');
      link.href = `#${id}`;
      link.textContent = heading.textContent.trim();
      item.appendChild(link);
      list.appendChild(item);
    });

    if (!('IntersectionObserver' in window)) return;
    const links = [...list.querySelectorAll('a')];
    const setActive = (id) => {
      links.forEach((link) => {
        const active = link.getAttribute('href') === `#${id}`;
        link.classList.toggle('is-active', active);
        if (active) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
    };

    const observer = new IntersectionObserver((entries) => {
      const visible = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (visible[0]) setActive(visible[0].target.id);
    }, { rootMargin: '-20% 0px -68% 0px', threshold: 0 });

    headings.forEach((heading) => observer.observe(heading));
  };

  const init = () => document.querySelectorAll('[data-ssa]').forEach(initArticle);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
